import { type useCopyToWorkspace } from '~/components/CopyToWorkspace/hooks/useCopyToWorkspace';
import { type ShowCustomFieldBulkEditModalParams } from '~/hooks/customFields/useShowPublicCustomFieldBulkEditModal';
import { type useCreateComment } from '~/swr-hooks/discussions/useCreateComment';
import { type usePublicSubmitNewDiscussion } from '~/swr-hooks/discussions/usePublicSubmitNewDiscussion';
import { type UpdateItemsCustomFieldValueParams } from '~/utils/CustomFields/useUpdateItemsCustomFieldValue';

const prefix = 'ssoCallback:';
const urlKey = `${prefix}:url`;
const dataKey = `${prefix}:data`;

type SSOCallbackActionsNames =
  | 'copy-to-air'
  | 'oauth-redirect'
  | 'create-public-discussion'
  | 'reply-to-public-comment'
  | 'bulk-edit-custom-field-value'
  | 'edit-custom-field-value';

type SSOCallbackActionBase<T> = {
  action: SSOCallbackActionsNames;
  data?: T;
};

type ReplyToPublicCommentData = Parameters<ReturnType<typeof useCreateComment>['createComment']>[0];

type SSOReplyToPublicCommentAction = {
  action: 'reply-to-public-comment';
} & SSOCallbackActionBase<ReplyToPublicCommentData>;

type CopyToAirData = Parameters<ReturnType<typeof useCopyToWorkspace>['copyToAir']>[0];

type SSOCopyToAirAction = {
  action: 'copy-to-air';
} & SSOCallbackActionBase<CopyToAirData>;

type SubmitNewPublicDiscussionData = Parameters<
  ReturnType<typeof usePublicSubmitNewDiscussion>['submitNewDiscussion']
>[0];

type SSOCreatePublicDiscussionAction = {
  action: 'create-public-discussion';
} & SSOCallbackActionBase<SubmitNewPublicDiscussionData>;

export type SSOBulkEditCustomFieldValueAction = {
  action: 'bulk-edit-custom-field-value';
} & SSOCallbackActionBase<ShowCustomFieldBulkEditModalParams>;

export type SSOEditCustomFieldValueAction = {
  action: 'edit-custom-field-value';
} & SSOCallbackActionBase<UpdateItemsCustomFieldValueParams>;

type SSORedirectAction = {
  action: 'oauth-redirect';
};

export type SSOCallbackAction =
  | SSOCopyToAirAction
  | SSORedirectAction
  | SSOCreatePublicDiscussionAction
  | SSOReplyToPublicCommentAction
  | SSOBulkEditCustomFieldValueAction
  | SSOEditCustomFieldValueAction;

export type SSOCallbackData = {
  url: string;
  action?: SSOCallbackAction;
};

export const saveSSOCallbackData = ({ action, url }: SSOCallbackData) => {
  sessionStorage.setItem(urlKey, url);
  if (action) {
    sessionStorage.setItem(dataKey, JSON.stringify(action));
  }
};

export const clearSSOCallbackData = () => {
  sessionStorage.removeItem(urlKey);
  sessionStorage.removeItem(dataKey);
};

export const getSSORedirectUrl = () => sessionStorage.getItem(urlKey);

export const getSSOCallbackData = (): SSOCallbackAction | null => {
  const data = sessionStorage.getItem(dataKey);

  if (data) {
    return JSON.parse(data);
  }

  return null;
};
